/**
 * New Collection Sheet
 * Sheet/drawer for creating a new collection with tabs:
 * - Collection Setup (name, primary key, singleton)
 * - Optional System Fields (status, sort, created/updated tracking)
 */

import { useState } from 'react';
import { ArrowRight, Check, Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useCreateCollection } from '@/hooks/useCollections';
import { toast } from 'sonner';

interface NewCollectionSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: (collectionName: string) => void;
}

// Primary key type options
const PRIMARY_KEY_TYPES = [
    { value: 'auto_int', label: 'Auto Increment Integer' },
    { value: 'auto_big_int', label: 'Auto Increment Big Integer' },
    { value: 'uuid', label: 'Generated UUID' },
    { value: 'manual', label: 'Manual String' },
] as const;

// System fields configuration
interface SystemFieldConfig {
    enabled: boolean;
    name: string;
    label: string;
}

interface SystemFieldsState {
    status: SystemFieldConfig;
    sort: SystemFieldConfig;
    dateCreated: SystemFieldConfig;
    userCreated: SystemFieldConfig;
    dateUpdated: SystemFieldConfig;
    userUpdated: SystemFieldConfig;
}

const getDefaultSystemFields = (): SystemFieldsState => ({
    status: { enabled: false, name: 'status', label: 'Status' },
    sort: { enabled: false, name: 'sort', label: 'Sort' },
    dateCreated: { enabled: false, name: 'date_created', label: 'Created On' },
    userCreated: { enabled: false, name: 'user_created', label: 'Created By' },
    dateUpdated: { enabled: false, name: 'date_updated', label: 'Updated On' },
    userUpdated: { enabled: false, name: 'user_updated', label: 'Updated By' },
});

export function NewCollectionSheet({ open, onOpenChange, onSuccess }: NewCollectionSheetProps) {
    const [currentTab, setCurrentTab] = useState('collection_setup');
    const [collectionName, setCollectionName] = useState('');
    const [singleton, setSingleton] = useState(false);
    const [primaryKeyName, setPrimaryKeyName] = useState('id');
    const [primaryKeyType, setPrimaryKeyType] = useState<string>('auto_int');
    const [systemFields, setSystemFields] = useState<SystemFieldsState>(getDefaultSystemFields());

    const createCollection = useCreateCollection();

    const isValid = collectionName.trim().length > 0 && /^[a-z_][a-z0-9_]*$/i.test(collectionName);

    const toggleSystemField = (key: keyof SystemFieldsState) => {
        setSystemFields((prev) => ({
            ...prev,
            [key]: { ...prev[key], enabled: !prev[key].enabled },
        }));
    };

    const updateSystemFieldName = (key: keyof SystemFieldsState, name: string) => {
        setSystemFields((prev) => ({
            ...prev,
            [key]: { ...prev[key], name },
        }));
    };

    const resetForm = () => {
        setCurrentTab('collection_setup');
        setCollectionName('');
        setSingleton(false);
        setPrimaryKeyName('id');
        setPrimaryKeyType('auto_int');
        setSystemFields(getDefaultSystemFields());
    };

    const handleClose = () => {
        resetForm();
        onOpenChange(false);
    };

    const handleNext = () => {
        if (currentTab === 'collection_setup' && isValid) {
            setCurrentTab('optional_fields');
        }
    };

    const buildPrimaryKeyField = () => {
        switch (primaryKeyType) {
            case 'uuid':
                return {
                    field: primaryKeyName,
                    type: 'uuid',
                    meta: { hidden: true, readonly: true, interface: 'input', special: ['uuid'] },
                    schema: { is_primary_key: true, length: 36, has_auto_increment: false },
                };
            case 'manual':
                return {
                    field: primaryKeyName,
                    type: 'string',
                    meta: { interface: 'input', readonly: false, hidden: false },
                    schema: { is_primary_key: true, length: 255, has_auto_increment: false },
                };
            case 'auto_big_int':
                return {
                    field: primaryKeyName,
                    type: 'bigInteger',
                    meta: { hidden: true, interface: 'input', readonly: true },
                    schema: { is_primary_key: true, has_auto_increment: true },
                };
            default: // auto_int
                return {
                    field: primaryKeyName,
                    type: 'integer',
                    meta: { hidden: true, interface: 'input', readonly: true },
                    schema: { is_primary_key: true, has_auto_increment: true },
                };
        }
    };

    const buildSystemFields = () => {
        const fields: Array<Record<string, unknown>> = [];

        if (systemFields.status.enabled) {
            fields.push({
                field: systemFields.status.name,
                type: 'string',
                meta: {
                    width: 'full',
                    options: {
                        choices: [
                            { text: 'Published', value: 'published', color: 'var(--primary)' },
                            { text: 'Draft', value: 'draft', color: 'var(--foreground)' },
                            { text: 'Archived', value: 'archived', color: 'var(--warning)' },
                        ],
                    },
                    interface: 'select-dropdown',
                    display: 'labels',
                },
                schema: { default_value: 'draft', is_nullable: false },
            });
        }

        if (systemFields.sort.enabled) {
            fields.push({
                field: systemFields.sort.name,
                type: 'integer',
                meta: { interface: 'input', hidden: true },
                schema: {},
            });
        }

        if (systemFields.dateCreated.enabled) {
            fields.push({
                field: systemFields.dateCreated.name,
                type: 'timestamp',
                meta: { special: ['date-created'], interface: 'datetime', readonly: true, hidden: true, width: 'half' },
                schema: {},
            });
        }

        if (systemFields.userCreated.enabled) {
            fields.push({
                field: systemFields.userCreated.name,
                type: 'uuid',
                meta: { special: ['user-created'], interface: 'select-dropdown-m2o', readonly: true, hidden: true, width: 'half' },
                schema: {},
            });
        }

        if (systemFields.dateUpdated.enabled) {
            fields.push({
                field: systemFields.dateUpdated.name,
                type: 'timestamp',
                meta: { special: ['date-updated'], interface: 'datetime', readonly: true, hidden: true, width: 'half' },
                schema: {},
            });
        }

        if (systemFields.userUpdated.enabled) {
            fields.push({
                field: systemFields.userUpdated.name,
                type: 'uuid',
                meta: { special: ['user-updated'], interface: 'select-dropdown-m2o', readonly: true, hidden: true, width: 'half' },
                schema: {},
            });
        }

        return fields;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleCreate = () => {
        const payload = {
            collection: collectionName,
            fields: [buildPrimaryKeyField(), ...buildSystemFields()],
            schema: {},
            meta: {
                singleton,
                sort_field: systemFields.sort.enabled ? systemFields.sort.name : undefined,
                archive_field: systemFields.status.enabled ? systemFields.status.name : undefined,
                archive_value: systemFields.status.enabled ? 'archived' : undefined,
                unarchive_value: systemFields.status.enabled ? 'draft' : undefined,
            },
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        createCollection.mutate(payload as any, {
            onSuccess: () => {
                toast.success('Collection created successfully');
                handleClose();
                onSuccess?.(collectionName);
            },
            onError: (error: Error) => {
                toast.error(`Failed to create collection: ${error.message}`);
            },
        });
    };

    const systemFieldKeys: (keyof SystemFieldsState)[] = ['status', 'sort', 'dateCreated', 'userCreated', 'dateUpdated', 'userUpdated'];

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent className="w-[600px] sm:max-w-[600px]">
                <SheetHeader>
                    <SheetTitle>Creating New Collection</SheetTitle>
                    <SheetDescription>
                        Configure your new collection's settings and optional system fields
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6">
                    <Tabs value={currentTab} onValueChange={setCurrentTab}>
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="collection_setup">Collection Setup</TabsTrigger>
                            <TabsTrigger value="optional_fields" disabled={!isValid}>
                                Optional System Fields
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="collection_setup" className="mt-6 space-y-6">
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
                                Collections are containers for data. Each collection represents a database table.
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                {/* Collection Name */}
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1">
                                        Name
                                        <Star className="h-3 w-3 fill-primary text-primary" />
                                    </Label>
                                    <Input
                                        value={collectionName}
                                        onChange={(e) => {
                                            // Auto-replace spaces with underscores
                                            const value = e.target.value.replace(/ /g, '_');
                                            setCollectionName(value);
                                        }}
                                        placeholder="a_unique_table_name"
                                        className="font-mono"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Collection names are case-sensitive
                                    </p>
                                </div>

                                {/* Singleton */}
                                <div className="space-y-2">
                                    <Label>Singleton</Label>
                                    <div className="flex items-center space-x-2">
                                        <Switch checked={singleton} onCheckedChange={setSingleton} />
                                        <span className="text-sm text-muted-foreground">
                                            Treat as single object
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-border pt-6" />

                            <div className="grid grid-cols-2 gap-6">
                                {/* Primary Key Field */}
                                <div className="space-y-2">
                                    <Label>Primary Key Field</Label>
                                    <Input
                                        value={primaryKeyName}
                                        onChange={(e) => setPrimaryKeyName(e.target.value)}
                                        placeholder="id"
                                        className="font-mono"
                                    />
                                </div>

                                {/* Primary Key Type */}
                                <div className="space-y-2">
                                    <Label>Type</Label>
                                    <Select value={primaryKeyType} onValueChange={setPrimaryKeyType}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PRIMARY_KEY_TYPES.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="optional_fields" className="mt-6 space-y-6">
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
                                System fields are automatically managed by the system. Enable the fields you need.
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {systemFieldKeys.map((key) => {
                                    const field = systemFields[key];
                                    const isDisabled = singleton && key === 'sort';
                                    return (
                                        <div
                                            key={key}
                                            className={cn(
                                                'space-y-2 rounded-lg border p-4',
                                                field.enabled
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-border'
                                            )}
                                        >
                                            <div className="flex items-center justify-between">
                                                <Label className="font-medium">{field.label}</Label>
                                                <Switch
                                                    checked={field.enabled}
                                                    onCheckedChange={() => toggleSystemField(key)}
                                                    disabled={isDisabled}
                                                />
                                            </div>
                                            <Input
                                                value={field.name}
                                                onChange={(e) => updateSystemFieldName(key, e.target.value)}
                                                className="font-mono text-sm"
                                                disabled={!field.enabled}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Actions */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-end gap-2 border-t border-border bg-background p-4">
                    <Button variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>

                    {currentTab === 'collection_setup' ? (
                        <Button onClick={handleNext} disabled={!isValid}>
                            Next
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleCreate}
                            disabled={createCollection.isPending}
                        >
                            {createCollection.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Check className="mr-2 h-4 w-4" />
                                    Finish Setup
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
